import axios, { AxiosInstance, AxiosError } from 'axios';
import { useAuthStore } from '../stores/authStore';

const API_URL = import.meta.env.VITE_API_URL || 'http://localhost:5000/api/v1';

// Create axios instance
const api: AxiosInstance = axios.create({
  baseURL: API_URL,
  headers: {
    'Content-Type': 'application/json',
  },
  timeout: 30000,
});

// Request interceptor
api.interceptors.request.use(
  (config) => {
    const token = useAuthStore.getState().token;

    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }

    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Response interceptor
api.interceptors.response.use(
  (response) => {
    return response.data;
  },
  (error: AxiosError) => {
    // Handle 401 Unauthorized
    if (error.response?.status === 401) {
      useAuthStore.getState().logout();
      window.location.href = '/login';
    }

    // Handle other errors
    const message =
      (error.response?.data as any)?.error?.message ||
      error.message ||
      'An error occurred';

    return Promise.reject(new Error(message));
  }
);

export default api;

// API service functions
export const authService = {
  login: (email: string, password: string) =>
    api.post('/auth/login', { email, password }),

  register: (data: {
    email: string;
    password: string;
    firstName: string;
    lastName: string;
    organizationName: string;
  }) => api.post('/auth/register', data),

  logout: () => api.post('/auth/logout'),

  refreshToken: () => api.post('/auth/refresh'),

  forgotPassword: (email: string) =>
    api.post('/auth/forgot-password', { email }),

  resetPassword: (token: string, password: string) =>
    api.post('/auth/reset-password', { token, password }),
};

export const userService = {
  getMe: () => api.get('/users/me'),
  updateMe: (data: any) => api.put('/users/me', data),
  getUsers: () => api.get('/users'),
  createUser: (data: any) => api.post('/users', data),
  updateUser: (id: string, data: any) => api.put(`/users/${id}`, data),
  deleteUser: (id: string) => api.delete(`/users/${id}`),
};

export const organizationService = {
  getCurrent: () => api.get('/organizations/current'),
  updateCurrent: (data: any) => api.put('/organizations/current', data),
  getSettings: () => api.get('/organizations/settings'),
  updateSettings: (data: any) => api.put('/organizations/settings', data),
};

// =====================================================
// Dashboards
// =====================================================
export const dashboardService = {
  getDashboards: (params?: any) => api.get('/dashboards', { params }),
  getDashboard: (dashboardId: string) => api.get(`/dashboards/${dashboardId}`),
  createDashboard: (data: any) => api.post('/dashboards', data),
  updateDashboard: (dashboardId: string, updates: any) => api.put(`/dashboards/${dashboardId}`, updates),
  deleteDashboard: (dashboardId: string) => api.delete(`/dashboards/${dashboardId}`),
  duplicateDashboard: (dashboardId: string, newName: string) =>
    api.post(`/dashboards/${dashboardId}/duplicate`, { newDashboardName: newName }),
  getWidgets: (dashboardId: string) => api.get(`/dashboards/${dashboardId}/widgets`),
  getWidgetData: (widgetId: string, params?: any) =>
    api.get(`/dashboards/widgets/${widgetId}/data`, { params }),
  getDashboardStats: (dashboardId?: string) =>
    api.get('/dashboards/stats/dashboards', { params: { dashboardId } }),
  getFavorites: () => api.get('/dashboards/favorites/my'),
  addFavorite: (dashboardId: string) => api.post('/dashboards/favorites', { dashboardId }),
  removeFavorite: (dashboardId: string) => api.delete(`/dashboards/favorites/${dashboardId}`),
};

// =====================================================
// KPIs
// =====================================================
export const kpiService = {
  getKPIs: (params?: any) => api.get('/kpis/definitions', { params }),
  getKPI: (kpiId: string) => api.get(`/kpis/definitions/${kpiId}`),
  createKPI: (data: any) => api.post('/kpis/definitions', data),
  updateKPI: (kpiId: string, updates: any) => api.put(`/kpis/definitions/${kpiId}`, updates),
  deleteKPI: (kpiId: string) => api.delete(`/kpis/definitions/${kpiId}`),
  recordValue: (kpiId: string, data: any) => api.post(`/kpis/definitions/${kpiId}/record`, data),
  getValues: (kpiId: string, params?: any) => api.get(`/kpis/definitions/${kpiId}/values`, { params }),
  getTrend: (kpiId: string, params: any) => api.get(`/kpis/definitions/${kpiId}/trend`, { params }),
  getDashboardKPIs: (dashboardId: string) => api.get(`/kpis/dashboards/${dashboardId}`),
  getTargets: (kpiId: string) => api.get(`/kpis/definitions/${kpiId}/targets`),
};

// =====================================================
// Budgets
// =====================================================
export const budgetService = {
  getAllocations: (params?: any) => api.get('/budgets/allocations', { params }),
  getAllocation: (budgetId: string) => api.get(`/budgets/allocations/${budgetId}`),
  createTopDownAllocation: (data: any) => api.post('/budgets/allocations/top-down', data),
  createBottomUpRequest: (data: any) => api.post('/budgets/allocations/bottom-up', data),
  updateAllocation: (budgetId: string, updates: any) => api.put(`/budgets/allocations/${budgetId}`, updates),
  deleteAllocation: (budgetId: string) => api.delete(`/budgets/allocations/${budgetId}`),
  approve: (budgetId: string, approved: boolean, comments?: string) =>
    api.post(`/budgets/allocations/${budgetId}/approve`, { approved, comments }),
  submit: (budgetId: string) => api.post(`/budgets/allocations/${budgetId}/submit`),
  finalize: (budgetId: string) => api.post(`/budgets/allocations/${budgetId}/finalize`),
  getSummary: (budgetId: string) => api.get(`/budgets/allocations/${budgetId}/summary`),
  getActuals: (params?: any) => api.get('/budgets/actuals', { params }),
};

// =====================================================
// Scenarios
// =====================================================
export const scenarioService = {
  getScenarios: (params?: any) => api.get('/scenarios', { params }),
  getScenario: (scenarioId: string) => api.get(`/scenarios/${scenarioId}`),
  createScenario: (data: any) => api.post('/scenarios', data),
  updateScenario: (scenarioId: string, updates: any) => api.put(`/scenarios/${scenarioId}`, updates),
  deleteScenario: (scenarioId: string) => api.delete(`/scenarios/${scenarioId}`),
  copyScenario: (sourceId: string, targetName: string) =>
    api.post(`/scenarios/${sourceId}/copy`, { targetScenarioName: targetName }),
  compareScenarios: (scenarioIds: string[]) => api.post('/scenarios/compare', { scenarioIds }),
  getAssumptions: (scenarioId: string) => api.get(`/scenarios/${scenarioId}/assumptions`),
};

// =====================================================
// Variance Analysis
// =====================================================
export const varianceService = {
  getAnalyses: (params?: any) => api.get('/variances', { params }),
  getAnalysis: (analysisId: string) => api.get(`/variances/${analysisId}`),
  generateAnalysis: (data: any) => api.post('/variances/generate', data),
  getLineItems: (analysisId: string) => api.get(`/variances/${analysisId}/line-items`),
  getTopVariances: (analysisId: string, limit: number = 10) =>
    api.get(`/variances/${analysisId}/top`, { params: { limit } }),
  getInsights: (analysisId: string) => api.get(`/variances/${analysisId}/insights`),
  addCommentary: (analysisId: string, data: any) =>
    api.post(`/variances/${analysisId}/commentary`, data),
};

// =====================================================
// Financial Statements
// =====================================================
export const financialStatementService = {
  getProfitLoss: (params?: any) => api.get('/profit-loss', { params }),
  getBalanceSheet: (params?: any) => api.get('/balance-sheet', { params }),
  getCashFlow: (params?: any) => api.get('/cash-flow', { params }),
};

// =====================================================
// Reports
// =====================================================
export const reportService = {
  getTemplates: (params?: any) => api.get('/reports/templates', { params }),
  getTemplate: (templateId: string) => api.get(`/reports/templates/${templateId}`),
  getDefinitions: (params?: any) => api.get('/reports/definitions', { params }),
  getDefinition: (definitionId: string) => api.get(`/reports/definitions/${definitionId}`),
  generateReport: (data: any) => api.post('/reports/generate', data),
  getInstances: (params?: any) => api.get('/reports/instances', { params }),
  getInstance: (instanceId: string) => api.get(`/reports/instances/${instanceId}`),
  getOutputs: (instanceId: string) => api.get(`/reports/instances/${instanceId}/outputs`),
  downloadOutput: (outputId: string) => api.get(`/reports/outputs/${outputId}/download`),
  getSchedules: (params?: any) => api.get('/reports/schedules', { params }),
  getStats: (params?: any) => api.get('/reports/stats/generation', { params }),
  getPopular: (limit: number = 10) => api.get('/reports/stats/popular', { params: { limit } }),
  getFavorites: () => api.get('/reports/favorites'),
};

// =====================================================
// Forecasts
// =====================================================
export const forecastService = {
  getRollingForecasts: (params?: any) => api.get('/forecasts/periods', { params }),
  getRollingForecast: (forecastId: string) => api.get(`/forecasts/periods/${forecastId}`),
  getCashFlowForecast: (forecastId: string) => api.get(`/forecasts/periods/${forecastId}/cash-flow`),
  getFinancialForecasts: (params?: any) => api.get('/financial-forecasts/models', { params }),
  getFinancialForecast: (modelId: string) => api.get(`/financial-forecasts/models/${modelId}`),
  consolidateForecast: (modelId: string) =>
    api.post(`/financial-forecasts/models/${modelId}/consolidate`),
};

// =====================================================
// Workflows
// =====================================================
export const workflowService = {
  getMyTasks: (params?: any) => api.get('/workflows/tasks/my-tasks', { params }),
  getTask: (taskId: string) => api.get(`/workflows/tasks/${taskId}`),
  completeTask: (taskId: string, data: any) => api.post(`/workflows/tasks/${taskId}/complete`, data),
  assignTask: (taskId: string, userId: string) =>
    api.post(`/workflows/tasks/${taskId}/assign`, { assignedTo: userId }),
  getInstances: (params?: any) => api.get('/workflows/instances', { params }),
  getInstance: (instanceId: string) => api.get(`/workflows/instances/${instanceId}`),
  getTemplates: (params?: any) => api.get('/workflows/templates', { params }),
};

// =====================================================
// Data Exchange
// =====================================================
export const dataExchangeService = {
  getExportTemplates: (params?: any) => api.get('/data-exchange/export/templates', { params }),
  createExportJob: (data: any) => api.post('/data-exchange/export/jobs', data),
  getExportJobs: (params?: any) => api.get('/data-exchange/export/jobs', { params }),
  getExportJob: (jobId: string) => api.get(`/data-exchange/export/jobs/${jobId}`),
  getImportTemplates: (params?: any) => api.get('/data-exchange/import/templates', { params }),
  createImportJob: (data: any) => api.post('/data-exchange/import/jobs', data),
  getImportJobs: (params?: any) => api.get('/data-exchange/import/jobs', { params }),
  getImportJob: (jobId: string) => api.get(`/data-exchange/import/jobs/${jobId}`),
  getImportErrors: (jobId: string, limit?: number) =>
    api.get(`/data-exchange/import/jobs/${jobId}/errors`, { params: { limit } }),
  getExportStats: (params?: any) => api.get('/data-exchange/export/stats', { params }),
  getImportStats: (params?: any) => api.get('/data-exchange/import/stats', { params }),
  getHistory: (params?: any) => api.get('/data-exchange/history', { params }),
};

// =====================================================
// Consolidation
// =====================================================
export const consolidationService = {
  getGroups: (params?: any) => api.get('/consolidation/groups', { params }),
  getGroup: (groupId: string) => api.get(`/consolidation/groups/${groupId}`),
  getMembers: (groupId: string) => api.get(`/consolidation/groups/${groupId}/members`),
  getRuns: (params?: any) => api.get('/consolidation/runs', { params }),
  getRun: (runId: string) => api.get(`/consolidation/runs/${runId}`),
  getBalances: (runId: string, params?: any) =>
    api.get(`/consolidation/runs/${runId}/balances`, { params }),
  getSummary: (runId: string) => api.get(`/consolidation/runs/${runId}/summary`),
  getICTransactions: (params?: any) => api.get('/consolidation/ic-transactions', { params }),
  matchICTransactions: (periodDate: Date) =>
    api.post('/consolidation/ic-transactions/match', { periodDate }),
};
